import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { cn } from "@timeghost/ui/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { createContext, useContext } from "react";

type TabsOrientation = "horizontal" | "vertical";
type TabsVariant = "default" | "line";

const TabsOrientationContext = createContext<TabsOrientation>("horizontal");
const TabsVariantContext = createContext<TabsVariant>("default");

const tabsVariants = cva("flex gap-2", {
	variants: {
		orientation: {
			horizontal: "flex-col",
			vertical: "flex-row",
		},
	},
	defaultVariants: {
		orientation: "horizontal",
	},
});

const tabsListVariants = cva("inline-flex w-fit items-center justify-center text-muted-foreground", {
	variants: {
		variant: {
			/** Track sits above card/popover; active tab uses input. */
			default: "h-8 rounded-lg bg-well p-[3px]",
			line: "z-[1] h-auto w-full min-w-0 justify-start gap-0 rounded-none border-border border-b bg-transparent p-0",
		},
		orientation: {
			horizontal: "",
			vertical: "h-fit flex-col",
		},
	},
	defaultVariants: {
		variant: "default",
		orientation: "horizontal",
	},
});

const tabsTriggerVariants = cva(
	"relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-transparent font-medium text-sm outline-none focus-visible:border-ring focus-visible:outline-1 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"h-[calc(100%-1px)] flex-1 rounded-md px-1.5 py-0.5 text-foreground/60 transition-colors hover:text-foreground data-active:bg-input data-active:text-foreground data-active:shadow-sm dark:text-muted-foreground dark:data-active:border-input dark:data-active:text-foreground dark:hover:text-foreground",
				line: "h-auto flex-none rounded-none border-x-0 border-t-0 border-b-2 border-transparent bg-transparent px-3 py-3 font-normal text-muted-foreground shadow-none hover:text-foreground data-active:border-primary data-active:bg-transparent data-active:font-semibold data-active:text-foreground dark:data-active:border-primary dark:data-active:bg-transparent",
			},
			orientation: {
				horizontal: "",
				vertical: "w-full justify-start",
			},
		},
		defaultVariants: {
			variant: "default",
			orientation: "horizontal",
		},
	}
);

const tabsContentVariants = cva("flex-1 text-sm outline-none");

function Tabs({
	className,
	orientation = "horizontal",
	...props
}: TabsPrimitive.Root.Props & VariantProps<typeof tabsVariants>) {
	return (
		<TabsOrientationContext.Provider value={orientation}>
			<TabsPrimitive.Root
				className={cn(tabsVariants({ orientation }), className)}
				data-orientation={orientation}
				data-slot="tabs"
				orientation={orientation}
				{...props}
			/>
		</TabsOrientationContext.Provider>
	);
}

function TabsList({
	className,
	variant = "default",
	orientation,
	...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
	const contextOrientation = useContext(TabsOrientationContext);
	return (
		<TabsVariantContext.Provider value={variant ?? "default"}>
			<TabsPrimitive.List
				className={cn(tabsListVariants({ orientation: orientation ?? contextOrientation, variant }), className)}
				data-slot="tabs-list"
				{...props}
			/>
		</TabsVariantContext.Provider>
	);
}

function TabsTrigger({
	className,
	orientation,
	variant,
	...props
}: TabsPrimitive.Tab.Props & VariantProps<typeof tabsTriggerVariants>) {
	const contextOrientation = useContext(TabsOrientationContext);
	const listVariant = useContext(TabsVariantContext);
	return (
		<TabsPrimitive.Tab
			className={cn(
				tabsTriggerVariants({
					orientation: orientation ?? contextOrientation,
					variant: variant ?? listVariant,
				}),
				className
			)}
			data-slot="tabs-trigger"
			{...props}
		/>
	);
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
	return <TabsPrimitive.Panel className={cn(tabsContentVariants(), className)} data-slot="tabs-content" {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants };
